import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IAppointment,
  IState,
  IUser,
  TGetQuestionByIdResponse,
  TQuestion,
  TReportData,
} from "../types";
import { api } from "../api/api";
import { formatDateTimeInIsrael, prevencherId } from "../../utils/utils";
import { DataTypes } from "../../utils/constants";

const initialState: IState = {
  userId: "",
  user: null,
  questionnaire: undefined,
  questionnaireCompaniesId: 0,
  QuestionnaireCompaniesTypeId: 0,
  allQuestions: undefined,
  question: undefined,
  questionsOptions: [],
  messages: [],
  updates: [],
  currentQuestionId: -1,
  isEnd: false,
  error: "",
  allAnswers: [],
  allAudioAnswers: [],
  allFileAnswers: [],
  appointment: null,
  fakeCompanyId: null,
};

const updateStateFromPayload = (
  state: IState,
  payload: TGetQuestionByIdResponse
) => {
  if (!payload.Questions) {
    state.error = "משהו השתבש, אנא נסה שוב";
  } else {
    state.question = payload.Questions;
    state.currentQuestionId = payload.Questions?.Id;
    state.questionsOptions = payload?.QuestionsOptions || [];
    state.isEnd = payload?.Questions?.IsEnd;
    const index = state.messages.findIndex(
      (m) => m.messageId === payload.Questions.Id.toString()
    );
    // Only a "Sentence" can open a section: it asks for nothing, so the chat moves on by itself.
    const isSectionHeader =
      payload.Questions.DataTypesId === DataTypes.AutoAdvance &&
      !!payload.Questions.IsSectionHeader;
    const questionMessage = {
      messageId: payload.Questions.Id.toString(),
      id: prevencherId,
      date: formatDateTimeInIsrael(new Date()),
      message: payload.Questions.Desc,
      ...(isSectionHeader ? { isSectionHeader: true } : {}),
    };
    if (index === -1) {
      state.messages = [...state.messages, questionMessage];
    } else {
      state.messages = [...state.messages.slice(0, index), questionMessage];
    }
  }
};

export const slice = createSlice({
  name: "api",
  initialState,
  reducerPath: "api",
  reducers: {
    setMessages: (state: IState, { payload }) => {
      return {
        ...state,
        messages: [...state.messages, payload],
      };
    },
    updateMessage: (state: IState, { payload }: { payload: { questionId: number; newMessage: string } }) => {
      const messageIndex = state.messages.findIndex(
        (msg) => msg.questionId === payload.questionId && msg.id === "guest"
      );
      if (messageIndex !== -1) {
        state.messages[messageIndex] = {
          ...state.messages[messageIndex],
          message: payload.newMessage,
          messageId: `${payload.questionId}${payload.newMessage}`,
        };
      }
    },
    setOptions: (state: IState, { payload }) => {
      return {
        ...state,
        questionsOptions: payload,
      };
    },
    setAnswers: (state: IState, { payload }) => {
      const newAnswer = { ...payload, AnswerDate: null };

      if (newAnswer.QuestionsId !== "0") {
        const existingAnswerIndex = state.allAnswers.findIndex(
          (answer) =>
            answer.QuestionsId === newAnswer.QuestionsId && !answer.QuestionText
        );

        if (existingAnswerIndex !== -1) {
          state.allAnswers[existingAnswerIndex] = newAnswer;
        } else {
          state.allAnswers.push(newAnswer);
        }
      } else {
        state.allAnswers.push(newAnswer);
      }
    },
    setAudioAnswers: (
      state: IState,
      {
        payload,
      }: { payload: { questionId: number; file: Blob; questionText?: string } }
    ) => {
      return {
        ...state,
        allAudioAnswers: [...state.allAudioAnswers, payload],
      };
    },

    resetAudioAnswers: (state: IState) => {
      return {
        ...state,
        allAudioAnswers: [],
      };
    },

    setFileAnswers: (
      state: IState,
      { payload }: { payload: { questionId: number; file: File }[] }
    ) => {
      return {
        ...state,
        allFileAnswers: [...state.allFileAnswers, ...payload],
      };
    },

    resetFileAnswers: (state: IState) => {
      return {
        ...state,
        allFileAnswers: [],
      };
    },
    revertToQuestion: (state: IState, action: PayloadAction<number>) => {
      // הוספתי את הטיפוס הנכון ל-state
      const questionIdToRevert = action.payload;

      const lastRelevantMessageIndex = state.messages.findIndex(
        (msg) => msg.questionId === questionIdToRevert && msg.id === "guest"
      );

      if (lastRelevantMessageIndex !== -1) {
        state.messages.splice(lastRelevantMessageIndex + 1); // דרך יעילה יותר לחתוך את המערך
      }

      if (state.allQuestions) {
        const questionToRevert = state.allQuestions.find(
          (q) => q.Id === questionIdToRevert
        );
        if (questionToRevert) {
          const seqToRevertFrom = questionToRevert.Seq;
          state.allAnswers = state.allAnswers.filter((answer) => {
            const answerQuestion = state.allQuestions?.find(
              (q) => q.Id.toString() === answer.QuestionsId
            );
            return answerQuestion ? answerQuestion.Seq < seqToRevertFrom : true;
          });
        }
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addMatcher(
        api.endpoints.getCurrentQuestionnaireByName.matchFulfilled,
        (state: IState, { payload }: { payload: TReportData }) => {
          if (payload.error) {
            return {
              ...state,
              error: payload.error,
            };
          }
          return {
            ...state,
            questionnaire: payload.Questionnaire,
            allQuestions: payload.Questions.filter(q => q.StatusId != 0).sort((a, b) => a.Seq - b.Seq),
            questionnaireCompaniesId: payload.QuestionnaireCompanies.Id,
            QuestionnaireCompaniesTypeId:
              payload.QuestionnaireCompanies.QuestionnaireCompaniesTypeId ?? 0,
            fakeCompanyId:
              payload.FakeCompanyId && payload.FakeCompanyId > 0
                ? payload.FakeCompanyId
                : null,
          };
        }
      )
      .addMatcher(
        api.endpoints.getCurrentQuestionnaireByGuid.matchFulfilled,
        (state: IState, { payload }: { payload: TReportData }) => {
          if (payload.error) {
            return {
              ...state,
              error: payload.error,
            };
          }
          return {
            ...state,
            questionnaire: payload.Questionnaire,
            allQuestions: payload.Questions.filter(q => q.StatusId != 0).sort((a, b) => a.Seq - b.Seq),
            questionnaireCompaniesId: payload.QuestionnaireCompanies.Id,
            QuestionnaireCompaniesTypeId:
              payload.QuestionnaireCompanies.QuestionnaireCompaniesTypeId ?? 0,
            fakeCompanyId:
              payload.FakeCompanyId && payload.FakeCompanyId > 0
                ? payload.FakeCompanyId
                : null,
          };
        }
      )
      // A questionnaire named outright by a treatment protocol's link lands in exactly the same
      // state as one resolved by type, so everything downstream renders it without knowing which
      // kind of link brought it here.

      .addMatcher(
        api.endpoints.getQuestionnaireByComponent.matchFulfilled,
        (state: IState, { payload }: { payload: TReportData }) => {
          if (payload.error) {
            return {
              ...state,
              error: payload.error,
            };
          }
          return {
            ...state,
            questionnaire: payload.Questionnaire,
            allQuestions: payload.Questions.filter(q => q.StatusId != 0).sort((a, b) => a.Seq - b.Seq),
            questionnaireCompaniesId: payload.QuestionnaireCompanies.Id,
            QuestionnaireCompaniesTypeId:
              payload.QuestionnaireCompanies.QuestionnaireCompaniesTypeId ?? 0,
            fakeCompanyId:
              payload.FakeCompanyId && payload.FakeCompanyId > 0
                ? payload.FakeCompanyId
                : null,
          };
        }
      )
      .addMatcher(
        api.endpoints.getQuestionById.matchFulfilled,
        (state: IState, { payload }: { payload: TGetQuestionByIdResponse }) => {
          updateStateFromPayload(state, payload);
        }
      )
  },
});

export const { actions, reducer } = slice;
export default slice.reducer;
